/**
 * @file modules/shop_assistant.js
 * @description Webpacker port of app/assets/javascripts/components/shopAssistant.coffee
 *              so the Member Assistant chat widget works on reskin pages (which
 *              don't load the legacy application.js sprockets bundle).
 *
 *              Mounts on <div id="shop-assistant">, wires Send / New chat /
 *              toggle, POSTs to /api/v1/member_assistant/messages, and renders
 *              the assistant reply by CONSUMING the NDJSON stream shop-web
 *              returns ({"type":"delta"|"error"|"done", ...}), re-parsing the
 *              accumulated markdown on each delta.
 *
 *              Session identity is server-side (Rails encrypted session
 *              cookie); the widget is a dumb pipe and never sees it.
 *
 *              Visual/interaction design ported from the Nova redesign doc
 *              (claude.ai/design project "Chatbot UI/UX improvements", file
 *              "Shop Assistant Redesign.dc.html").
 */

import { marked } from "marked";
import DOMPurify from "dompurify";

const ENDPOINT = "/api/v1/member_assistant/messages";

// Shop has no client-side navigation, so each page load rebuilds the
// widget's DOM from scratch. sessionStorage lets us stitch the visible
// conversation back together on remount so a page click doesn't nuke
// the user's chat context. Per-tab scope matches the server-side
// session's lifetime (Rails encrypted cookie).
const STORAGE_KEY = "shop-assistant.state";

// How long an individual offer's "Opening {name}…" status stays up
// before clearing itself (see _activateOfferTarget). The Nova doc's own
// demo never clears this; a real click already opened a new tab, so
// there's nothing left to wait for — auto-clearing is a small, deliberate
// improvement over the literal doc.
const OFFER_STATUS_CLEAR_MS = 1500;

const EMPTY_STATE_SUGGESTIONS = [
  "What deals do I have?",
  "What are today's top cash back offers?",
  "How do I verify as military?",
];

const THUMBS_UP_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path></svg>';

const THUMBS_DOWN_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path></svg>';

// Refresh (circular-arrow) glyph for the header "New chat" button, per the
// Nova "Chat Open" design — matches fragno's own 24×24 stroke header icons
// (ICON_DOTS/ICON_CLOSE), so it lines up with the × close beside it.
const NEW_CHAT_REFRESH_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M23 4v6h-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// Chevrons for the carousel "‹ N of M ›" pager buttons.
const PAGER_CHEVRON_LEFT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>';

const PAGER_CHEVRON_RIGHT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>';

// Matches cash-back/discount phrasing the system prompt actually uses
// ("up to 10% off", "5% cash back", "$40 off") so _extractOfferCard can
// pull the value out of an offer's freeform description text.
const OFFER_AMOUNT_PATTERN =
  /(up to\s+)?\$?\d+(\.\d+)?%?\s*(cash\s*back|off|back)\b/i;

// Best-effort expiration phrase ("through Aug 3", "ends Aug 4") — offers
// don't always mention one, so a card with no match just shows no meta
// line.
const OFFER_DATE_PATTERN =
  /(?:ends|through|until)\s+([A-Z][a-z]{2,8}\.?\s*\d{1,2})/i;

// Community tag accent colors — an addition on top of the Nova doc
// (which shows "Military only" as plain gray meta text, identical to a
// date). Values are the actual --color-community-* tokens from the
// design system's fig-tokens.css. "senior"/"veteran"/"medical" aren't
// literal Figma community tokens — they're mapped to the nearest kin
// (alumni/military/health-care-provider) since the system prompt's own
// community list (AgentConfig.java) doesn't line up 1:1 with Figma's.
const COMMUNITY_TAG_COLORS = {
  military: "rgb(194, 58, 39)",
  veteran: "rgb(194, 58, 39)",
  teacher: "rgb(45, 146, 125)",
  nurse: "rgb(11, 160, 167)",
  medical: "rgb(154, 105, 196)",
  student: "rgb(37, 155, 223)",
  senior: "rgb(207, 132, 0)",
  "first responder": "rgb(45, 62, 81)",
};
const COMMUNITY_TAG_FALLBACK_COLOR = "rgb(46, 144, 250)";

const ROLE_CSS_CLASS = {
  user: "shop-assistant-msg-user",
  error: "shop-assistant-msg-error",
  system: "shop-assistant-msg-system",
};

// Read an application/x-ndjson response body as an async iterator of
// parsed JSON events. Buffers partial trailing lines across chunks —
// a single delta may split across two TCP frames, so we can't just
// JSON.parse each raw chunk. Yields one event per complete `\n`-
// terminated line; drops (but doesn't throw on) unparseable lines so a
// single wire-format hiccup doesn't kill the whole stream.
async function* readNdjsonEvents(body) {
  const reader = body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl;
      while ((nl = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, nl).trim();
        buffer = buffer.slice(nl + 1);
        if (line.length === 0) continue;
        try {
          yield JSON.parse(line);
        } catch (_) {
          // wire drift; keep reading
        }
      }
    }
    // Flush the decoder + any trailing bytes without a final `\n`.
    buffer += decoder.decode();
    const tail = buffer.trim();
    if (tail.length > 0) {
      try {
        yield JSON.parse(tail);
      } catch (_) {
        // trailing garbage — ignore
      }
    }
  } finally {
    // Ensure the underlying network resource is CANCELLED (not just
    // released) when the consumer stops iterating early — e.g. the
    // sendMessage for-await loop hits `break streamLoop` on an in-band
    // error envelope (Syed's PR #3708 S1). releaseLock() alone drops
    // this generator's grip on the reader but leaves the underlying
    // ReadableStream + fetch open, so the browser keeps buffering
    // further bytes after the widget stopped caring, until the server
    // finally closes. reader.cancel() aborts the fetch AND releases
    // the lock atomically.
    try {
      await reader.cancel();
    } catch (_) {
      // Reader already closed (normal end-of-stream reached the
      // `done: true` branch above) or the fetch was aborted for
      // another reason; either way, nothing to clean up.
    }
  }
}

// Pull a friendly error string out of a non-2xx shop-web response.
// Shop-web returns JSON error bodies with a `message` field for the
// pre-stream failure branches (400 blank/too-long, 429 throttle, 502
// upstream). Falls back to a generic message if the body isn't JSON.
async function extractErrorMessage(res) {
  try {
    const body = await res.json();
    if (body && typeof body.message === "string" && body.message.length > 0) {
      return body.message;
    }
  } catch (_) {
    // response wasn't JSON
  }
  return "Something went wrong. Please try again.";
}

class ShopAssistant {
  constructor(root) {
    this.root = root;
    // The chat UI is the Fragno chat-wrapper web component. It owns the
    // launcher/trigger, the header (title, ⋯ menu = New chat, × close), the
    // scrollable message thread, and the input bar. We drive it
    // imperatively: append message nodes to `thread`, and listen for the
    // wrapper's send/menu events.
    this.wrapper = root.querySelector("fragno-chat-wrapper");

    // The wrapper builds its thread + default input bar in
    // connectedCallback, so these exist only once it has upgraded and
    // connected. If they're missing, treat the widget as not-yet-ready and
    // let initShopAssistant's retry re-run us on the next tick (same race
    // the old HAML-timing retry covered).
    this.messages = this.wrapper?.thread ?? null;
    this.inputBar =
      this.wrapper?.querySelector("fragno-chat-input-bar") ?? null;

    // Bail out if the wrapper isn't ready. Without this, a stray HAML
    // change would throw during `addEventListener` and poison the rest of
    // the reskin JS bundle (every module downstream in packs/reskin.js
    // stops initializing).
    const required = [this.wrapper, this.messages, this.inputBar];
    if (required.some((el) => el == null)) {
      // eslint-disable-next-line no-console
      console.error("ShopAssistant: chat-wrapper not ready", { root });
      this.initialized = false;
      return;
    }
    this.initialized = true;

    // All listeners routed through this controller so `destroy()` can
    // tear them down together before a remount on the same element.
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Send: the wrapper re-dispatches its built-in input bar's send event
    // with the typed text in `detail.value`. Enter-to-send, Shift+Enter
    // newline, and send-button enable/disable are all handled inside the
    // input bar, so there's nothing left to wire here.
    this.wrapper.addEventListener(
      "fragno-chat-wrapper-send",
      (e) => this.sendMessage(e.detail?.value),
      { signal },
    );

    // New chat: the header's ⋯ menu is the single "more actions" affordance;
    // clicking it starts a fresh conversation.
    this.wrapper.addEventListener(
      "fragno-chat-wrapper-menu",
      () => this.clearChat(),
      { signal },
    );

    // fragno-chat-wrapper defaults to platform="mobile"; set it for the actual
    // device so desktop gets the desktop input/layout. Set once on mount — the
    // panel is docked, not live-resized.
    this.wrapper.setAttribute(
      "platform",
      window.innerWidth >= 600 ? "desktop" : "mobile",
    );

    // Re-add the "answers can be wrong" disclaimer below the input. The wrapper
    // has no footer slot, so inject it into the bottom region (after the input
    // slot); the old HAML rendered this and its SCSS is unchanged.
    this._renderDisclaimer();

    // Present the header's ⋯ "more actions" menu as a ↻ New-chat button — it
    // has exactly one action (start a fresh chat), so a dots menu is needless
    // indirection, and the Nova design uses a refresh glyph here. Click wiring
    // above (fragno-chat-wrapper-menu → clearChat) is unchanged; this only
    // swaps the glyph + label.
    this._restyleMenuAsNewChat();

    // Restore any prior conversation from sessionStorage BEFORE rendering
    // the empty state. Only fall back to the greeting when there was
    // nothing to restore (fresh tab, or after clearChat). The panel is NOT
    // opened here — the wrapper starts closed (see _restoreState).
    if (!this._restoreState()) {
      this._renderEmptyState();
    }
  }

  csrfToken() {
    return (
      document
        .querySelector("meta[name='csrf-token']")
        ?.getAttribute("content") ?? ""
    );
  }

  async sendMessage(text) {
    // Text comes from the wrapper's send event (detail.value); falling back
    // to the input bar's current value covers programmatic callers.
    const message = (text ?? this.inputBar.value ?? "").trim();
    if (!message) return;

    // Only relevant on the very first send — clears the empty-state
    // affordance chips so they don't linger next to a growing thread.
    this.messages.querySelector(".shop-assistant-suggestions")?.remove();

    this.appendMessage("user", message);
    // Clear the input bar; it self-disables its send button when empty.
    this.inputBar.value = "";
    // Remember the message so a failed turn's Retry can re-run it without
    // re-appending the user bubble.
    this._lastUserMessage = message;

    await this._runTurn(message);
  }

  // Runs one request/stream turn for `message`: typing indicator → POST to the
  // NDJSON endpoint → render the streamed reply (framing prose streamed live,
  // then any offer cards dropped in below it) or an error. Split from
  // sendMessage so an error bubble's Retry can re-invoke it directly (see
  // _renderMessage) without appending a second user bubble.
  async _runTurn(message) {
    this._showTyping();

    // Streaming assistant bubble is created LAZILY on the first prose delta,
    // so the typing indicator covers time-to-first-token rather than an empty
    // bubble sitting there. (A cards-only reply with no prose creates the
    // bubble at end-of-stream instead — see the post-loop block.)
    let streaming = null;
    let accumulated = "";
    let streamErrored = false;
    // Structured offer/store cards emitted by the search tools (real merchant
    // logos, tracked URLs, cashback). They arrive on the stream BEFORE the
    // prose deltas, but we BUFFER them here and render them ONCE at
    // end-of-stream, BELOW the framing prose — so the text streams in first and
    // the cards drop in beneath it, not the other way round. There may be more
    // than one per turn (offers and/or stores).
    const cardEnvelopes = [];

    // Create the assistant bubble exactly once, on the first renderable prose
    // delta, so the framing text owns bubble creation.
    const ensureBubble = () => {
      if (!streaming) {
        this._hideTyping();
        streaming = this._beginStreamingAssistant();
      }
      return streaming;
    };

    try {
      // POST to shop-web's NDJSON streaming endpoint. The response body
      // is a ReadableStream — read it chunk by chunk, split into NDJSON
      // lines, dispatch each line by `type` field.
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/x-ndjson",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ message }),
      });

      if (!res.ok) {
        // Pre-stream server-side failure (blank/too-long message → 400,
        // MAA-unreachable → 502, throttle → 429). Body is a normal JSON
        // error envelope; fall back to the existing error UX.
        this._hideTyping();
        this._appendRetryableError(await extractErrorMessage(res));
        return;
      }

      // Verify the content-type before we start consuming — a shop-web
      // regression that serves buffered JSON instead of NDJSON would
      // otherwise be silently swallowed as an empty stream.
      const contentType = res.headers.get("Content-Type") ?? "";
      if (!contentType.includes("application/x-ndjson")) {
        this._hideTyping();
        this._appendRetryableError(
          "Assistant response was in an unexpected format.",
        );
        return;
      }

      // H4 (Jay's PR review): `break` inside `switch` exits only the
      // switch, not the `for-await` loop — so a second event after
      // the error branch would fall through with `streaming = null`.
      // A realistic sequence (MAA errors then drops the socket ⇒
      // shop-web writes ECONNRESET-triggered `{"type":"error"}` at
      // controller.rb:305) or a trailing delta after error would
      // dereference null: `_updateStreamingAssistant(null, …)` →
      // TypeError, silently swallowed by the outer catch because
      // `streamErrored` is already true. Fix is a labeled break that
      // exits the loop cleanly + `streaming?.remove()` defensive
      // null-guard on the error path.
      streamLoop: for await (const event of readNdjsonEvents(res.body)) {
        if (!event || typeof event !== "object") continue;
        switch (event.type) {
          case "delta":
            if (typeof event.text === "string" && event.text.length > 0) {
              ensureBubble();
              accumulated += event.text;
              // Stream the framing prose live, per-delta, at the top of the
              // bubble. Cards are buffered and rendered below it at
              // end-of-stream (see the `cards` case + the post-loop block), so
              // the text always appears first and the cards drop in beneath it.
              this._updateStreamingAssistant(streaming, accumulated);
              // Card replies usually stream a markdown offer list inside their
              // framing prose that the real cards below will replace. Strip it
              // DURING streaming (not just at finalize) so it doesn't flash as a
              // "ghost" list while the stream runs. No-op until a full
              // offer-shaped list has accumulated; the cards envelope arrives
              // before the prose, so cardEnvelopes is already populated here.
              if (cardEnvelopes.length > 0) {
                this._stripProseOfferLists(streaming.contentEl);
              }
            }
            break;
          case "cards":
            // A search tool's structured results (real logos, tracked URLs,
            // cashback), arriving AHEAD of the prose on the wire. BUFFER them —
            // don't render or create the bubble here — so the framing prose
            // streams in first; the buffered cards are rendered below it once
            // the stream ends (post-loop block / error branch).
            if (Array.isArray(event.results) && event.results.length > 0) {
              cardEnvelopes.push({ tool: event.tool, results: event.results });
            }
            break;
          case "error":
            // Mid-stream failure envelope from shop-web or MAA.
            this._hideTyping();
            streamErrored = true;
            if (cardEnvelopes.length > 0) {
              // The search itself succeeded — only the prose generation died.
              // Salvage the buffered cards: ensure a bubble (a cards-only reply
              // that errored may have produced no prose delta yet), drop the
              // cards below whatever framing prose streamed, and surface the
              // failure as a SEPARATE error bubble rather than dropping the
              // whole reply. `streaming` stays set so the `finally` persists it.
              ensureBubble();
              this._renderBufferedCards(streaming, cardEnvelopes);
            } else {
              // No cards to salvage — discard the partial bubble.
              streaming?.remove();
              streaming = null;
            }
            this._appendRetryableError(
              "The assistant lost its connection. Please try again.",
            );
            break streamLoop; // exit for-await, not just the switch
          case "done":
            // No-op on the client — session_id continuity is server-side
            // (Rails encrypted session cookie). The `done` envelope is
            // still useful as a signal that the stream ended cleanly.
            break;
          default:
            // Ignore unknown types — forward compatible with future
            // envelope additions (e.g. tool-status events).
            break;
        }
      }

      this._hideTyping();
      if (!streamErrored) {
        const hasCards = cardEnvelopes.length > 0;
        if (!streaming && !hasCards) {
          // Stream closed without a single delta, without any cards, and
          // without an error envelope — a malformed reply. Same UX as the
          // pre-streaming "empty answer" branch.
          this._appendRetryableError(
            "Received an empty response. Please try again.",
          );
        } else {
          // A cards-only reply produces no prose delta, so no bubble exists
          // yet — create it now to hold the cards.
          if (!streaming) {
            streaming = this._beginStreamingAssistant();
          }
          if (hasCards) {
            // The framing prose (if any) already streamed live into the
            // content div; now drop the buffered cards below it, strip any
            // prose offer list that duplicates them, lift the citation footer
            // below them, and attach feedback thumbs. Stash the envelopes so
            // _extractMessages can persist them (a refresh restores the cards
            // WITH their real logos, not the prose-scrape letter-avatar path).
            this._renderBufferedCards(streaming, cardEnvelopes);
          } else {
            // No-card reply (FAQ / help answer): the prose streamed live; just
            // promote any prose offer list, lift citations, attach feedback.
            this._finalizeStreamedMessage(streaming.element, {
              hasCards: false,
            });
          }
        }
      }
    } catch (_err) {
      // A real throw (network drop mid-read, JSON parse error on a malformed
      // line, or any other unexpected failure) — as opposed to an in-band
      // `error` envelope, which the switch handles + breaks the loop cleanly.
      this._hideTyping();
      if (!streamErrored) {
        if (cardEnvelopes.length > 0) {
          // The search succeeded before the stream threw — salvage the
          // buffered cards below whatever prose streamed, mirroring the
          // `error`-envelope path, rather than dropping the whole reply.
          ensureBubble();
          this._renderBufferedCards(streaming, cardEnvelopes);
        } else if (streaming) {
          streaming.remove();
          streaming = null;
        }
        this._appendRetryableError("Something went wrong. Please try again.");
      }
    } finally {
      // Defensive: guarantee no orphan typing indicator survives on any
      // exit path (idempotent — a no-op if already hidden).
      this._hideTyping();
      // Persist the final assistant message (if any) once the stream
      // completes, rather than on every delta — sessionStorage writes
      // are relatively expensive and per-delta persistence would burn
      // main-thread time during rapid token bursts. (Error/user bubbles
      // persist themselves via appendMessage.)
      if (streaming) {
        this._persistState();
      }
    }
  }

  // Render a retryable error bubble — fragno's inline agent-status error with a
  // Retry action that re-runs the last turn. Used for every _runTurn failure
  // (non-2xx, bad content-type, mid-stream error, empty stream, network drop).
  _appendRetryableError(text) {
    this.appendMessage("error", text, false, { retryable: true });
  }

  // Retry a failed turn: drop the error bubble and re-run the request for the
  // last user message. The user bubble stays, so nothing is duplicated.
  _retryLastTurn(errorBubble) {
    errorBubble?.remove();
    if (this._lastUserMessage) this._runTurn(this._lastUserMessage);
  }

  async clearChat() {
    // Server owns the chat session; wait for the DELETE to confirm before
    // blanking the UI so a failed request leaves the visible thread in sync
    // with the server-side session (no ghost session).
    try {
      const res = await fetch(ENDPOINT, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
      });
      if (res.ok) {
        this.messages.replaceChildren();
        // Drop any stored state before rendering the fresh greeting so
        // the persisted view matches the DOM. Skipping this would leave
        // the pre-clear conversation in storage until the next message
        // triggered a fresh `_persistState()` write.
        sessionStorage.removeItem(STORAGE_KEY);
        this._renderEmptyState();
      } else {
        this.appendMessage(
          "error",
          "Couldn't start a new chat. Please try again.",
        );
      }
    } catch (_err) {
      this.appendMessage(
        "error",
        "Couldn't start a new chat. Please try again.",
      );
    }
  }

  // Create an empty assistant message element and return handles for
  // subsequent streaming updates. Intentionally content-only (no feedback
  // thumbs yet) — thumbs are attached in _finalizeStreamedMessage once the
  // stream completes, so they can land inside the offer actions row when
  // the reply carries offer cards (matching the non-streaming path).
  // Envelope cards are appended as SIBLINGS of the content div by
  // _appendEnvelopeCards, so the per-delta content re-render doesn't wipe
  // them.
  _beginStreamingAssistant() {
    const msg = document.createElement("div");
    msg.className = "shop-assistant-msg shop-assistant-msg-assistant";
    msg.dataset.rawText = "";
    msg.innerHTML = `<div class="shop-assistant-msg-content"></div>`;
    this.messages.appendChild(msg);
    this.messages.scrollTop = this.messages.scrollHeight;

    const contentEl = msg.querySelector(".shop-assistant-msg-content");
    return {
      element: msg,
      contentEl,
      remove: () => msg.remove(),
    };
  }

  // Re-render the streaming assistant message's content on every delta.
  // Full-buffer re-parse via marked+DOMPurify+link-rewrite is what
  // ChatGPT / Claude / Perplexity all do — cheap for small buffers
  // (~1ms) and users don't notice the brief flicker on unclosed markdown
  // (`[label](htt` etc.) that resolves once the closing chars arrive.
  // Offer/citation promotion is intentionally skipped here (see
  // renderAssistantBody) and applied once at end-of-stream instead.
  _updateStreamingAssistant(streaming, fullText) {
    streaming.element.dataset.rawText = fullText;
    streaming.contentEl.innerHTML = this.renderAssistantBody(fullText);
    this.messages.scrollTop = this.messages.scrollHeight;
  }

  // Panel open/close is owned by the chat-wrapper (it also handles the
  // trigger, Escape-to-close, and focus management). These stay as thin
  // delegators for programmatic callers. Open state is deliberately not
  // persisted — it is never restored on a fresh mount (see _restoreState).
  openPanel() {
    this.wrapper.open();
  }

  closePanel() {
    this.wrapper.close();
  }

  // The old widget rendered a "answers can be wrong" disclaimer under the
  // composer. fragno-chat-wrapper exposes no footer slot, so append it into the
  // bottom region (below the input slot). Idempotent.
  _renderDisclaimer() {
    const bottom = this.wrapper.querySelector(".fragno-chat-wrapper__bottom");
    if (!bottom || bottom.querySelector(".shop-assistant-disclaimer")) return;
    const p = document.createElement("p");
    p.className = "shop-assistant-disclaimer";
    p.textContent =
      "Answers can be wrong. Check the offer terms before you shop.";
    bottom.appendChild(p);
  }

  // Turn fragno's header ⋯ menu button into a ↻ New-chat button. fragno
  // renders it as a fragno-button with an `icon-leading` SVG + a `label`
  // (both observed attributes), so swapping them re-renders the glyph and
  // updates the accessible name without touching the click wiring
  // (fragno-chat-wrapper-menu → clearChat). The button lives in the wrapper's
  // light DOM (no `shadow` attr on the host), reachable like the disclaimer's
  // bottom region. Idempotent — safe if the wrapper ever re-inits.
  _restyleMenuAsNewChat() {
    const menuBtn = this.wrapper.querySelector(".fragno-chat-wrapper__menu");
    if (!menuBtn) return;
    menuBtn.setAttribute("icon-leading", NEW_CHAT_REFRESH_SVG);
    menuBtn.setAttribute("label", "New chat");
    menuBtn.setAttribute("title", "New chat");
  }

  destroy() {
    this.abortController?.abort();
    // No production caller: each navigation discards the document. The
    // test harness calls this to remount on the same element, so the DOM
    // stays in place and the mount guard is cleared for the next init.
    this.root.dataset.shopAssistantMounted = "false";
  }

  renderAssistantBody(text) {
    // Agent replies are markdown. Render to HTML, then sanitize so we never
    // inject anything that wasn't on DOMPurify's allow-list.
    // breaks:false — single newlines collapse to spaces (CommonMark default).
    //
    // NOTE: this stays a pure markdown→sanitized-HTML transform so it's
    // cheap to call on every streaming delta. Citation promotion is applied
    // separately (_finalizeStreamedMessage) once the stream finishes. Prose
    // offer-list stripping runs at finalize too — and, for card replies, also
    // per-delta during streaming (see the delta case) so the model's own offer
    // list doesn't ghost above the buffered cards.
    const rawHtml = marked.parse(text, { gfm: true, breaks: false });
    const cleaned = DOMPurify.sanitize(rawHtml, {
      USE_PROFILES: { html: true },
    });
    // Route every link out to a new tab, matching the rest of shop's
    // offer/store links. Done here (post-sanitize) rather than as a
    // DOMPurify hook so it never introduces attributes onto a tag that
    // DOMPurify would otherwise have stripped.
    const container = document.createElement("div");
    container.innerHTML = cleaned;
    container.querySelectorAll("a[href]").forEach((link) => {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    });
    return container.innerHTML;
  }

  // Replaces every offer-shaped bullet list (every <li> starts with an
  // anchor — the system prompt's `[Merchant](url) — description`
  // format) with either a stacked list (≤3 offers — reads like a direct
  // recommendation) or a horizontally-scrolling card carousel (4+ —
  // browsable). The "**X member offers**" marker line (AgentConfig.java's
  // community-scoped-offers rule (e)) tags its offers with a community
  // badge; everything else falls into one combined display in document
  // order, matching rule (a)'s "community offers first" framing.
  // Locate the offer-shaped prose lists in a rendered reply: every <ul>
  // whose every <li> starts with an anchor (the system prompt's
  // "[Merchant](url) — description" citation format), plus the optional
  // "**X member offers**" community heading that may sit above one of
  // them. Shared by the prose-scraping path (_promoteOfferDisplay) and the
  // structured-envelope path (_stripProseOfferLists, which removes these
  // once the cards have been rendered from the envelope).
  _findProseOfferLists(container) {
    const badgePara = Array.from(container.querySelectorAll("p")).find((p) => {
      const isSoleStrongChild =
        p.childNodes.length === 1 && p.firstElementChild?.tagName === "STRONG";
      return (
        isSoleStrongChild && /^.+ member offers$/i.test(p.textContent.trim())
      );
    });
    const isOfferShaped = (list) =>
      list.children.length > 0 &&
      Array.from(list.children).every(
        (li) => li.firstChild instanceof HTMLAnchorElement,
      );
    // Match both <ul> and <ol> — the model sometimes returns offers as a
    // numbered list, which would otherwise survive the strip and render
    // alongside the cards.
    const offerUls = Array.from(container.querySelectorAll("ul, ol")).filter(
      isOfferShaped,
    );
    return { offerUls, badgePara };
  }

  // Strip the "Other offers you might like:" style transition sentences
  // wedged between two consecutive offer lists — once the lists collapse
  // into a single card display those paragraphs are left stranded.
  _stripInterListParagraphs(offerUls) {
    for (let i = 1; i < offerUls.length; i += 1) {
      let node = offerUls[i].previousElementSibling;
      while (node && node !== offerUls[i - 1] && node.tagName === "P") {
        const toRemove = node;
        node = node.previousElementSibling;
        toRemove.remove();
      }
    }
  }

  _promoteOfferDisplay(container) {
    const { offerUls, badgePara } = this._findProseOfferLists(container);
    if (offerUls.length === 0) return;
    const communityLabel = badgePara
      ? badgePara.textContent.trim().replace(/ member offers$/i, " only")
      : null;
    const communityUl =
      badgePara?.nextElementSibling?.tagName === "UL"
        ? badgePara.nextElementSibling
        : null;

    this._stripInterListParagraphs(offerUls);

    const cards = [];
    offerUls.forEach((ul) => {
      const label = ul === communityUl ? communityLabel : null;
      Array.from(ul.children).forEach((li) =>
        cards.push(this._extractOfferCard(li, label)),
      );
    });
    if (cards.length === 0) return;

    const display =
      cards.length <= 3
        ? this._buildOfferList(cards)
        : this._buildOfferCarousel(cards);
    offerUls[0].replaceWith(display);
    offerUls.slice(1).forEach((ul) => ul.remove());
    badgePara?.remove();
  }

  // Render the buffered "cards" envelopes below the (already-streamed) framing
  // prose and finalize the message. Shared by _runTurn's end-of-stream success
  // path and its mid-stream-error card-salvage path: append each envelope's
  // cards as siblings after the content div, strip any duplicate prose offer
  // list, lift the citation footer below the cards, attach feedback thumbs, and
  // stash the envelopes so _extractMessages can persist them (a refresh
  // restores the cards WITH real logos, not the prose-scrape letter avatars).
  _renderBufferedCards(streaming, cardEnvelopes) {
    cardEnvelopes.forEach((env) =>
      this._appendEnvelopeCards(streaming.element, env),
    );
    this._finalizeStreamedMessage(streaming.element, { hasCards: true });
    streaming.element.dataset.cardEnvelopes = JSON.stringify(cardEnvelopes);
  }

  // Render ONE "cards" envelope's results (real merchant logos, tracked
  // URLs, cashback) as a card display and append it to the assistant
  // bubble, AFTER the prose content div so the framing text sits above the
  // cards. The content div is re-rendered wholesale on every prose delta (see
  // _updateStreamingAssistant), so the cards must live as SIBLINGS of it —
  // not inside it. Any prose offer list that Gemini also writes is stripped
  // post-stream (_stripProseOfferLists) so offers never render twice.
  _appendEnvelopeCards(msg, { tool, results }) {
    const cards = results.map((r) => this._envelopeResultToCard(tool, r));
    if (cards.length === 0) return;
    const display =
      cards.length <= 3
        ? this._buildOfferList(cards)
        : this._buildOfferCarousel(cards);
    msg.appendChild(display);
    // Wire now, while attached — _bindCarouselPager reads offsetLeft, which
    // is 0 on a detached node.
    this._wireOfferInteractions(display);
  }

  // Map one "cards" envelope result onto the card shape the builders
  // consume ({ merchantName, href, amount, tag, meta, logo }). `tag` is
  // always null: the only community signal on the wire is
  // `affinity_group_uid` (a UID with no client-side name map), and
  // labeling an offer "members only" when we can't confirm exclusivity
  // would mislead — so we surface no community tag on this path.
  _envelopeResultToCard(tool, r) {
    const href = this._sanitizeHref(r.url ?? "") ?? "#";
    const logo = this._sanitizeLogoUrl(r.logo);
    const cashBack = typeof r.cash_back === "string" ? r.cash_back.trim() : "";
    const cashBackMeta =
      r.has_cash_back === true && cashBack ? `${cashBack} cash back` : null;

    if (tool === "search_stores") {
      const offerCount = typeof r.offer_count === "number" ? r.offer_count : 0;
      const description =
        typeof r.description === "string" ? r.description.trim() : "";
      return {
        merchantName: (r.name ?? "").trim(),
        href,
        // Stores lead with the cashback rate; the offer count / blurb
        // rides the meta line.
        amount: r.has_cash_back === true && cashBack ? cashBack : "",
        tag: null,
        meta:
          offerCount > 0
            ? `${offerCount} offer${offerCount === 1 ? "" : "s"}`
            : description || null,
        logo,
      };
    }

    // search_offers (default): `title` is the offer headline
    // ("Up to 10% off"); cashback goes in the meta line.
    return {
      merchantName: (r.merchant ?? "").trim(),
      href,
      amount: typeof r.title === "string" ? r.title.trim() : "",
      tag: null,
      meta: cashBackMeta,
      logo,
    };
  }

  // A logo URL is only usable as an <img src> if it's http(s). Returns ""
  // for null/blank/non-http so the caller falls back to the initial-letter
  // avatar. (Server-side logoUrlOrEmpty already blanks the generic
  // placeholder; this is client-side defense in depth.)
  _sanitizeLogoUrl(url) {
    if (typeof url !== "string" || url.trim().length === 0) return "";
    return this._sanitizeHref(url) ?? "";
  }

  // Pulls the pieces of one offer card out of its <li>: the merchant
  // name + href from the leading anchor (per the system prompt's
  // citation rule #2 format), an amount if the description mentions one,
  // the community tag (if this card came from the community-scoped
  // list), and a best-effort expiration phrase.
  _extractOfferCard(li, communityLabel) {
    const anchor = li.firstChild;
    const href = this._sanitizeHref(anchor.getAttribute("href")) ?? "#";
    const merchantName = anchor.textContent.trim();
    const descriptionText = li.textContent
      .slice(anchor.textContent.length)
      .trim();

    const amountMatch = descriptionText.match(OFFER_AMOUNT_PATTERN);
    const amount = amountMatch ? amountMatch[0] : null;

    const dateMatch = descriptionText.match(OFFER_DATE_PATTERN);
    const meta = dateMatch ? `Ends ${dateMatch[1]}` : null;

    return { merchantName, href, amount, tag: communityLabel, meta };
  }

  _buildOfferCarousel(cards) {
    const wrap = document.createElement("div");
    wrap.className = "shop-assistant-carousel-wrap";

    const track = document.createElement("div");
    track.className = "shop-assistant-carousel-track";
    cards.forEach((card) => track.appendChild(this._buildOfferCard(card)));
    wrap.appendChild(track);

    // "‹  N of M  ›" pager (replaces the old proportional progress rail): the
    // prev/next buttons scroll the track one card at a time and the label
    // tracks the current card. Wired in _bindCarouselPager once attached
    // (card offsetLeft reads 0 while the node is still detached).
    wrap.appendChild(this._buildCarouselPager(cards.length));

    wrap.appendChild(this._buildOfferStatus());
    wrap.appendChild(
      this._buildOfferActions(
        "See all offers",
        "shop-assistant-offer-action-secondary",
      ),
    );

    return wrap;
  }

  // The "‹  1 of N  ›" pager row under the carousel. Total is the card count;
  // the label starts at card 1 and _bindCarouselPager keeps it in sync with
  // the track's scroll position.
  _buildCarouselPager(total) {
    const pager = document.createElement("div");
    pager.className = "shop-assistant-carousel-pager";

    const prev = document.createElement("button");
    prev.type = "button";
    prev.className =
      "shop-assistant-carousel-pager-btn shop-assistant-carousel-pager-prev";
    prev.setAttribute("aria-label", "Previous offer");
    prev.innerHTML = PAGER_CHEVRON_LEFT_SVG;
    pager.appendChild(prev);

    const label = document.createElement("span");
    label.className = "shop-assistant-carousel-pager-label";
    // Announce the position change to screen readers as the user pages.
    label.setAttribute("aria-live", "polite");
    label.textContent = `1 of ${total}`;
    pager.appendChild(label);

    const next = document.createElement("button");
    next.type = "button";
    next.className =
      "shop-assistant-carousel-pager-btn shop-assistant-carousel-pager-next";
    next.setAttribute("aria-label", "Next offer");
    next.innerHTML = PAGER_CHEVRON_RIGHT_SVG;
    pager.appendChild(next);

    return pager;
  }

  // ≤3 offers — a full-width stacked list instead of the carousel, per
  // AgentConfig.java rule (a) still putting community offers first.
  _buildOfferList(cards) {
    const wrap = document.createElement("div");
    wrap.className = "shop-assistant-offer-list-wrap";

    const list = document.createElement("div");
    list.className = "shop-assistant-offer-list";
    cards.forEach((card) => list.appendChild(this._buildOfferRow(card)));
    wrap.appendChild(list);

    wrap.appendChild(this._buildOfferStatus());
    wrap.appendChild(
      this._buildOfferActions(
        "View offers",
        "shop-assistant-offer-action-primary",
      ),
    );

    return wrap;
  }

  // Empty by default — _activateOfferTarget fills it in ("Opening
  // {Merchant}…") when any card/row in this group is clicked, then
  // clears it again after OFFER_STATUS_CLEAR_MS.
  _buildOfferStatus() {
    const status = document.createElement("p");
    status.className = "shop-assistant-offer-status";
    return status;
  }

  _buildOfferActions(label, styleClass) {
    const actions = document.createElement("div");
    actions.className = "shop-assistant-carousel-actions";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `shop-assistant-offer-action-btn ${styleClass}`;
    btn.textContent = label;
    actions.appendChild(btn);
    return actions;
  }

  // The leading avatar for a card/row: the real merchant logo as an <img>
  // when the envelope supplied a safe http(s) URL, otherwise the
  // initial-letter placeholder. `variant` is "card" or "row" (they differ
  // only in size). A logo that 404s — merchant logos rot over time — swaps
  // itself back to the letter avatar via onerror, so the card degrades to
  // a clean initial rather than a broken-image icon.
  _buildAvatar(card, variant) {
    const base = `shop-assistant-offer-${variant}-avatar`;
    const letter = () => {
      const el = document.createElement("div");
      el.className = base;
      el.textContent = (card.merchantName || "?").charAt(0).toUpperCase();
      el.setAttribute("aria-hidden", "true");
      return el;
    };
    if (!card.logo) return letter();

    const img = document.createElement("img");
    img.className = `${base}-img`;
    img.src = card.logo;
    img.alt = "";
    img.loading = "lazy";
    img.setAttribute("aria-hidden", "true");
    img.addEventListener("error", () => img.replaceWith(letter()), {
      once: true,
    });
    return img;
  }

  // 200px offer card, per the Nova "Chat Open" design: a top row of the
  // circular merchant logo + store name, then the offer headline (up to two
  // lines) and a grey "Plus {secondary benefit}" line. The whole card is the
  // click target (opens the offer) — _wireOfferInteractions/_activateOfferTarget
  // handle the tab-open + shared status line, so there's no real href here.
  _buildOfferCard(card) {
    const el = document.createElement("button");
    el.type = "button";
    el.className = "shop-assistant-offer-card";
    el.dataset.href = card.href;
    el.dataset.merchantName = card.merchantName;
    // Fold the headline + cashback into the label — the button's aria-label
    // otherwise overrides its text content, so the clamped headline/secondary
    // would reach neither screen readers nor (via the visible text) sighted
    // users.
    el.setAttribute("aria-label", this._offerAriaLabel(card));

    // Top: circular logo + store name (+ optional community tag) beside it.
    const top = document.createElement("div");
    top.className = "shop-assistant-offer-card-top";
    top.appendChild(this._buildAvatar(card, "card"));

    const nameWrap = document.createElement("div");
    nameWrap.className = "shop-assistant-offer-card-namewrap";
    const name = document.createElement("div");
    name.className = "shop-assistant-offer-card-name";
    name.textContent = card.merchantName;
    nameWrap.appendChild(name);
    if (card.tag) nameWrap.appendChild(this._buildOfferTag(card.tag));
    top.appendChild(nameWrap);
    el.appendChild(top);

    // Bottom: the offer headline, then the grey "Plus {secondary}" line.
    // When there's no headline (e.g. a store with no cashback) but there is a
    // secondary detail, promote that detail to the headline so the card never
    // shows a blank primary line, and drop the "Plus" line.
    const headlineText = card.amount || card.meta || "";
    const showSecondary = Boolean(card.amount && card.meta);

    const bottom = document.createElement("div");
    bottom.className = "shop-assistant-offer-card-bottom";

    const headline = document.createElement("div");
    headline.className = "shop-assistant-offer-card-headline";
    headline.textContent = headlineText;
    bottom.appendChild(headline);

    if (showSecondary) {
      const secondary = document.createElement("div");
      secondary.className = "shop-assistant-offer-card-secondary";
      secondary.textContent = `Plus ${card.meta}`;
      bottom.appendChild(secondary);
    }
    el.appendChild(bottom);

    return el;
  }

  // "View the {Merchant} offer — {amount}, {meta}" — the amount/meta are
  // folded in because the card/row button's aria-label suppresses its own
  // text content from the accessibility tree.
  _offerAriaLabel(card) {
    return (
      `View the ${card.merchantName} offer` +
      (card.amount ? ` — ${card.amount}` : "") +
      (card.meta ? `, ${card.meta}` : "")
    );
  }

  _buildOfferRow(card) {
    const el = document.createElement("button");
    el.type = "button";
    el.className = "shop-assistant-offer-row";
    el.dataset.href = card.href;
    el.dataset.merchantName = card.merchantName;
    el.setAttribute("aria-label", this._offerAriaLabel(card));

    el.appendChild(this._buildAvatar(card, "row"));

    const text = document.createElement("div");
    text.className = "shop-assistant-offer-row-text";
    const name = document.createElement("div");
    name.className = "shop-assistant-offer-row-name";
    name.textContent = card.merchantName;
    text.appendChild(name);
    if (card.tag) text.appendChild(this._buildOfferTag(card.tag));
    if (card.meta) {
      const meta = document.createElement("div");
      meta.className = "shop-assistant-offer-row-meta";
      meta.textContent = card.meta;
      text.appendChild(meta);
    }
    el.appendChild(text);

    if (card.amount) {
      const amount = document.createElement("div");
      amount.className = "shop-assistant-offer-row-amount";
      amount.textContent = card.amount;
      el.appendChild(amount);
    }

    el.appendChild(this._buildChevronIcon());

    return el;
  }

  _buildOfferTag(label) {
    const tag = document.createElement("span");
    tag.className = "shop-assistant-offer-tag";
    tag.textContent = label;
    const accent = this._communityTagColor(label);
    tag.style.color = accent;
    tag.style.background = `color-mix(in srgb, ${accent} 12%, white)`;
    return tag;
  }

  _communityTagColor(label) {
    const lower = label.toLowerCase();
    const key = Object.keys(COMMUNITY_TAG_COLORS).find((k) =>
      lower.includes(k),
    );
    return key ? COMMUNITY_TAG_COLORS[key] : COMMUNITY_TAG_FALLBACK_COLOR;
  }

  _buildChevronIcon() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", "shop-assistant-offer-row-chevron");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    svg.setAttribute("aria-hidden", "true");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "m9 18 6-6-6-6");
    svg.appendChild(path);
    return svg;
  }

  // Opens the merchant link in a new tab and updates the offer group's
  // single shared status line to "Opening {Merchant}…" (matching the
  // Nova doc's hasOpened/opened state) rather than swapping text inside
  // the individual card — the design shows one status line per group,
  // not per card.
  _activateOfferTarget(wrap, merchantName, href) {
    window.open(href, "_blank", "noopener,noreferrer");
    const status = wrap.querySelector(".shop-assistant-offer-status");
    if (!status) return;
    window.clearTimeout(status._clearTimer);
    status.textContent = `Opening ${merchantName}…`;
    status._clearTimer = window.setTimeout(() => {
      status.textContent = "";
    }, OFFER_STATUS_CLEAR_MS);
  }

  // Ties the "‹  N of M  ›" pager to the track's scroll position: the
  // prev/next buttons scroll one card at a time, and the label reports the
  // current card while the ends disable the button that can't move further.
  // Must run after the track is attached to the live DOM — offsetLeft /
  // scrollWidth read 0 on a detached node.
  _bindCarouselPager(track) {
    const wrap = track.closest(".shop-assistant-carousel-wrap");
    const label = wrap?.querySelector(".shop-assistant-carousel-pager-label");
    const prev = wrap?.querySelector(".shop-assistant-carousel-pager-prev");
    const next = wrap?.querySelector(".shop-assistant-carousel-pager-next");
    const cards = track.querySelectorAll(".shop-assistant-offer-card");
    const total = cards.length;
    if (!label || total === 0) return;

    // Distance between one card's start and the next (card width + gap).
    // Falls back to the viewport width when there's only a single card.
    const step = () =>
      cards.length > 1
        ? cards[1].offsetLeft - cards[0].offsetLeft
        : track.clientWidth;

    // Boundary flags come from the browser's real scroll extent (which includes
    // the track's left/right padding); a 1px slack absorbs sub-pixel offsets.
    // The label index normally comes from scrollLeft / step, BUT at the true
    // scroll maximum `atEnd` OVERRIDES it to total-1 — otherwise rounding can
    // land on total-2 there and the label ("3 of 4") would disagree with an
    // already-disabled Next button (Rob's W1).
    const currentState = () => {
      const atStart = track.scrollLeft <= 1;
      const atEnd =
        track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      let index;
      if (atEnd) {
        index = total - 1;
      } else {
        const s = step();
        const raw = s > 0 ? Math.round(track.scrollLeft / s) : 0;
        index = Math.min(total - 1, Math.max(0, raw));
      }
      return { index, atStart, atEnd };
    };

    let lastIndex = -1;
    const update = () => {
      const { index, atStart, atEnd } = currentState();
      // Hand focus off BEFORE disabling the focused button — disabling the
      // element that currently has focus drops focus to <body> mid-interaction
      // (a keyboard user paging to the last card would lose their place). W2.
      if (atEnd && next && document.activeElement === next) prev?.focus();
      if (atStart && prev && document.activeElement === prev) next?.focus();
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
      // Only rewrite the aria-live label when the card index actually changes,
      // so one smooth-scroll (many scroll events) announces once, not per frame
      // (Rob's S1).
      if (index !== lastIndex) {
        lastIndex = index;
        label.textContent = `${index + 1} of ${total}`;
      }
    };

    prev?.addEventListener("click", () =>
      track.scrollBy({ left: -step(), behavior: "smooth" }),
    );
    next?.addEventListener("click", () =>
      track.scrollBy({ left: step(), behavior: "smooth" }),
    );
    track.addEventListener("scroll", update, { passive: true });
    update();
  }

  // Turns the trailing "Source:"/"Sources:" line(s) the system prompt
  // mandates (AgentConfig.java's citation rules) into a distinct
  // footer row instead of an inline text blob.
  _promoteCitations(container) {
    const citationPara = Array.from(container.querySelectorAll("p")).find((p) =>
      /^sources?:/i.test(p.textContent.trim()),
    );
    if (!citationPara) return;

    const isPlural = /^sources:/i.test(citationPara.textContent.trim());
    const hrefs = new Set();
    citationPara.querySelectorAll("a[href]").forEach((a) => {
      const clean = this._sanitizeHref(a.getAttribute("href"));
      if (clean) hrefs.add(clean);
    });
    // Fallback for when marked didn't auto-link the bare URL: pull it
    // straight out of the paragraph's text.
    (citationPara.textContent.match(/https?:\/\/[^\s)]+/g) ?? []).forEach(
      (raw) => {
        const clean = this._sanitizeHref(raw.replace(/[).,]+$/, ""));
        if (clean) hrefs.add(clean);
      },
    );
    if (hrefs.size === 0) return;

    const citationsEl = document.createElement("div");
    citationsEl.className = "shop-assistant-citations";

    const label = document.createElement("span");
    label.className = "shop-assistant-citations-label";
    label.textContent = isPlural ? "Sources" : "Source";
    citationsEl.appendChild(label);

    hrefs.forEach((href) => {
      const link = document.createElement("a");
      link.className = "shop-assistant-citation-link";
      link.href = href;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.title = href;
      link.textContent = this._deriveCitationLabel(href);
      citationsEl.appendChild(link);
    });

    citationPara.replaceWith(citationsEl);
  }

  // Citation URLs arrive bare (no markdown link title) — derive a
  // readable label from the last URL path segment rather than showing
  // the raw link. Not a "real" title, just the same information
  // reformatted; the full URL is still in the link's `title` attribute
  // and its actual href.
  _deriveCitationLabel(href) {
    try {
      const { pathname } = new URL(href);
      const segments = pathname.split("/").filter(Boolean);
      const last = segments[segments.length - 1] ?? "";
      const cleaned = last
        .replace(/\.\w+$/, "")
        .replace(/[-_]+/g, " ")
        .trim();
      if (!cleaned) return href;
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    } catch (_) {
      return href;
    }
  }

  // Defense-in-depth allowlist for any href we construct ourselves
  // (rather than one DOMPurify already vetted) — mirrors the backend's
  // http(s)-only sanitizeUrl guard against e.g. javascript: schemes.
  _sanitizeHref(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.protocol === "http:" || parsed.protocol === "https:"
        ? parsed.href
        : null;
    } catch (_) {
      return null;
    }
  }

  // showFeedback: whether this message should ever show thumbs at all —
  // true for real answers, false for the greeting (nothing to rate
  // about "hi").
  appendMessage(role, text, showFeedback = false, opts = {}) {
    this._renderMessage(role, text, undefined, showFeedback, undefined, opts);
    this._persistState();
  }

  // Split from appendMessage so `_restoreState` can rebuild the DOM
  // from previously-persisted messages without triggering another
  // `_persistState()` write on every message it replays.
  _renderMessage(
    role,
    text,
    persistedFeedback,
    showFeedback,
    cardEnvelopes,
    opts = {},
  ) {
    let msg;

    if (role === "assistant") {
      msg = document.createElement("div");
      msg.className = "shop-assistant-msg shop-assistant-msg-assistant";
      // Store the raw markdown text on the element so `_extractMessages`
      // can rebuild storage state without having to reverse-engineer
      // rendered HTML back into markdown.
      msg.dataset.rawText = text;
      msg.innerHTML = `<div class="shop-assistant-msg-content">${this.renderAssistantBody(text)}</div>`;
      // Must be appended first — _bindCarouselPager reads offsetLeft, which
      // is 0 on a detached node.
      this.messages.appendChild(msg);
      if (Array.isArray(cardEnvelopes) && cardEnvelopes.length > 0) {
        // Restoring a persisted reply that carried structured offer cards:
        // rebuild them from the saved envelopes (real logos + tracked URLs),
        // mirroring the live streaming path — NOT the prose-scrape fallback.
        cardEnvelopes.forEach((env) => this._appendEnvelopeCards(msg, env));
        const contentEl = msg.querySelector(".shop-assistant-msg-content");
        if (contentEl) {
          this._stripProseOfferLists(contentEl);
          this._promoteCitations(contentEl);
        }
        this._moveCitationsBelowCards(msg);
        if (showFeedback) this._attachFeedback(msg, persistedFeedback);
        // Re-stash so a later re-persist (e.g. a feedback toggle) keeps them.
        msg.dataset.cardEnvelopes = JSON.stringify(cardEnvelopes);
      } else {
        // FAQ-only reply, or a restored reply with no cards: prose-scrape +
        // citation/feedback path.
        this._decorateAssistantMessage(msg, {
          showFeedback,
          persistedFeedback,
        });
      }
    } else {
      // user / error / system → fragno-chat-bubble. The plain-text `message`
      // attribute is rendered via textContent by the component (XSS-safe), so
      // the visual treatment now comes from fragno.css and the old
      // .shop-assistant-msg-{user,error} box rules were dropped. The
      // .shop-assistant-msg + role marker classes stay for _extractMessages /
      // tests, and the raw text is stashed on data-raw-text for reliable
      // extraction (a rendered web component's textContent is not).
      msg = document.createElement("fragno-chat-bubble");
      msg.className = `shop-assistant-msg ${ROLE_CSS_CLASS[role] ?? ""}`.trim();
      msg.dataset.role = role;
      msg.dataset.rawText = text;
      msg.setAttribute("message", text);
      if (role === "error") {
        // Inline agent-status error; retryable ones (live _runTurn failures)
        // get a Retry action wired back to re-run the last turn. Restored
        // errors aren't retryable (no last-turn state after a reload).
        msg.setAttribute("type", "status");
        msg.setAttribute("error", "");
        if (opts.retryable) {
          msg.setAttribute("retry-label", "Retry");
          msg.addEventListener(
            "fragno-chat-bubble-retry-click",
            () => this._retryLastTurn(msg),
            { signal: this.abortController.signal },
          );
        }
      } else if (role === "user") {
        msg.setAttribute("type", "user");
      } else {
        // system (rare) — a plain agent-style bubble.
        msg.setAttribute("type", "agent");
      }
      this.messages.appendChild(msg);
    }

    this.messages.scrollTop = this.messages.scrollHeight;
    return msg;
  }

  // Post-render enrichment for the NON-streaming paths (_renderMessage /
  // _restoreState — restored sessionStorage replies, whose card envelopes
  // weren't persisted, plus any FAQ-only reply). Operates on an already-
  // attached assistant message whose `.shop-assistant-msg-content` holds
  // plain rendered markdown: scrape offer-shaped prose into cards (initial-
  // letter avatars, no logos), lift the citation footer, attach feedback
  // thumbs, and wire interactions. The LIVE streaming path renders envelope
  // cards at end-of-stream instead (see _renderBufferedCards /
  // _finalizeStreamedMessage).
  _decorateAssistantMessage(
    msg,
    { showFeedback = false, persistedFeedback } = {},
  ) {
    const contentEl = msg.querySelector(".shop-assistant-msg-content");
    if (contentEl) {
      this._promoteOfferDisplay(contentEl);
      this._promoteCitations(contentEl);
    }
    if (showFeedback) this._attachFeedback(msg, persistedFeedback);
    this._wireOfferInteractions(msg);
  }

  // Post-stream finalize for the LIVE streaming path. The envelope cards have
  // already been appended (and wired) below the prose by _renderBufferedCards,
  // so this only strips any prose offer list that duplicates them, lifts the
  // citation footer, and attaches feedback thumbs — no card rendering/re-wiring.
  _finalizeStreamedMessage(msg, { hasCards = false } = {}) {
    const contentEl = msg.querySelector(".shop-assistant-msg-content");
    if (contentEl) {
      if (hasCards) {
        // Structured cards already appended — just remove the duplicate prose
        // list (also stripped per-delta during streaming; idempotent here).
        this._stripProseOfferLists(contentEl);
      } else {
        // No cards envelope (e.g. the reply came back as a markdown offer
        // list): promote that list into cards on the LIVE path too, not
        // only after a refresh.
        this._promoteOfferDisplay(contentEl);
        this._wireOfferInteractions(msg);
      }
      this._promoteCitations(contentEl);
    }
    // Citations get promoted to the end of the content div, which sits
    // ABOVE the appended cards — move the footer below them so the order
    // reads framing → cards → citations.
    this._moveCitationsBelowCards(msg);
    this._attachFeedback(msg);
  }

  // When envelope cards were appended as direct children of the bubble
  // (siblings after the content div), the citations footer — promoted to the
  // end of the content div — ends up above them. Move it to the end of the
  // bubble so the order reads framing → cards → citations. Only fires for
  // envelope (sibling) cards; the prose-scrape path renders cards inside the
  // content div, where citations are already correctly ordered.
  _moveCitationsBelowCards(msg) {
    const hasEagerCards = Array.from(msg.children).some(
      (c) =>
        c.classList.contains("shop-assistant-carousel-wrap") ||
        c.classList.contains("shop-assistant-offer-list-wrap"),
    );
    const citations = msg.querySelector(".shop-assistant-citations");
    if (hasEagerCards && citations) msg.appendChild(citations);
  }

  // Remove any offer-shaped prose <ul> from streamed markdown once the
  // structured cards have been rendered separately, so offers don't appear
  // both as cards and as a text list.
  _stripProseOfferLists(container) {
    const { offerUls, badgePara } = this._findProseOfferLists(container);
    if (offerUls.length === 0) return;
    this._stripInterListParagraphs(offerUls);
    offerUls.forEach((ul) => ul.remove());
    badgePara?.remove();
  }

  // Attach feedback thumbs to an assistant message. When the reply carries
  // offer cards, the thumbs go into that display's actions row (the Nova
  // doc puts them beside "View offers"/"Show more"); otherwise a standalone
  // block below the text.
  _attachFeedback(msg, persistedFeedback) {
    const feedback = this._buildFeedbackThumbs(persistedFeedback);
    const actionsRow = msg.querySelector(".shop-assistant-carousel-actions");
    if (actionsRow) {
      actionsRow.appendChild(feedback);
    } else {
      msg.appendChild(feedback);
    }
    msg.querySelectorAll(".shop-assistant-feedback-btn").forEach((btn) => {
      btn.addEventListener("click", (e) => this.handleFeedback(e));
    });
  }

  // Wire the interactive bits of an offer display: the "See all offers" /
  // "any more?" button, the carousel pager, and per-card click-to-open. Scoped
  // to whatever root is passed — a freshly-built display (end-of-stream
  // buffered-cards path) or a whole message (the restore path) — so each
  // element is wired exactly once by whoever builds it.
  _wireOfferInteractions(root) {
    root.querySelectorAll(".shop-assistant-offer-action-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        // "Opening…" mirrors the Nova doc's loading state; re-asking the
        // same query is a normal follow-up turn — AgentConfig.java's
        // existing "any more?" handling re-calls the same tool and
        // paginates server-side, so there's no widget-side offset to
        // track, and no "N offers now showing" claim to fake (this widget
        // never hands off to a Shop-grid page).
        btn.disabled = true;
        btn.textContent = "Opening…";
        this.sendMessage("any more?");
      });
    });

    // Must run while attached — offsetLeft/scrollWidth on a still-detached
    // node both read as 0.
    root.querySelectorAll(".shop-assistant-carousel-track").forEach((track) => {
      this._bindCarouselPager(track);
    });
    root
      .querySelectorAll(".shop-assistant-offer-card, .shop-assistant-offer-row")
      .forEach((el) => {
        el.addEventListener("click", () => {
          const wrap = el.closest(
            ".shop-assistant-carousel-wrap, .shop-assistant-offer-list-wrap",
          );
          if (wrap)
            this._activateOfferTarget(
              wrap,
              el.dataset.merchantName,
              el.dataset.href,
            );
        });
      });
  }

  _buildFeedbackThumbs(selectedFeedback) {
    const wrap = document.createElement("div");
    wrap.className = "shop-assistant-feedback";

    const thumbs = document.createElement("div");
    thumbs.className = "shop-assistant-feedback-thumbs";

    const upSelected = selectedFeedback === "helpful";
    const downSelected = selectedFeedback === "not-helpful";

    const upBtn = document.createElement("button");
    upBtn.type = "button";
    upBtn.className = `shop-assistant-feedback-btn${upSelected ? " shop-assistant-feedback-btn--selected" : ""}`;
    upBtn.dataset.feedback = "helpful";
    upBtn.title = "Helpful";
    upBtn.setAttribute("aria-label", "Helpful");
    upBtn.setAttribute("aria-pressed", String(upSelected));
    upBtn.innerHTML = THUMBS_UP_SVG;
    thumbs.appendChild(upBtn);

    const downBtn = document.createElement("button");
    downBtn.type = "button";
    downBtn.className = `shop-assistant-feedback-btn${downSelected ? " shop-assistant-feedback-btn--selected" : ""}`;
    downBtn.dataset.feedback = "not-helpful";
    downBtn.title = "Not helpful";
    downBtn.setAttribute("aria-label", "Not helpful");
    downBtn.setAttribute("aria-pressed", String(downSelected));
    downBtn.innerHTML = THUMBS_DOWN_SVG;
    thumbs.appendChild(downBtn);

    wrap.appendChild(thumbs);

    if (selectedFeedback) {
      wrap.appendChild(this._buildFeedbackThanks());
    }

    return wrap;
  }

  _buildFeedbackThanks() {
    const thanks = document.createElement("span");
    thanks.setAttribute("role", "status");
    thanks.className = "shop-assistant-feedback-thanks";
    thanks.textContent = "Thanks — this helps.";
    return thanks;
  }

  // Transient "thinking" row shown while a request is in flight (up to
  // time-to-first-token, after which the streaming bubble takes over).
  // Deliberately NOT a `.shop-assistant-msg` — that class is what
  // `_extractMessages` scans for, and this element must never get
  // persisted to sessionStorage (it has no real content, and it's
  // always removed again before the next persist-triggering action).
  _showTyping() {
    // fragno's inline agent-status bubble in its loading (spinner) state.
    // Deliberately NOT a `.shop-assistant-msg` — like the suggestions row it
    // must never be persisted (see _extractMessages), and it's always removed
    // again before the real reply is appended.
    const el = document.createElement("fragno-chat-bubble");
    el.setAttribute("type", "status");
    el.setAttribute("loading", "");
    el.dataset.typingIndicator = "true";
    this.messages.appendChild(el);
    this.messages.scrollTop = this.messages.scrollHeight;
    return el;
  }

  _hideTyping() {
    this.messages.querySelector('[data-typing-indicator="true"]')?.remove();
  }

  _renderEmptyState() {
    // Render the greeting directly (not via appendMessage) so we can tag it
    // ephemeral BEFORE the persist runs — otherwise the greeting would be
    // saved and, on refresh, restore as a lone message that suppresses the
    // chips. No showFeedback — nothing to rate about a greeting.
    const greeting = this._renderMessage(
      "assistant",
      "Hi! I'm the Shop Assistant — here's how I can help:\n\n" +
        "- **Find deals and offers** from stores on ID.me Shop\n" +
        "- **Look up stores and cash back rates** so you can compare\n" +
        "- **Answer eligibility questions** — who qualifies and how to verify (military, teachers, students, and more)\n\n" +
        "What are you shopping for?",
      undefined,
      false,
    );
    greeting.dataset.ephemeral = "true";
    this._renderSuggestions();
    // No persist here: the greeting + chips are ephemeral, so there is
    // nothing to save (a _persistState() would only write messages:[]).
  }

  // Clickable affordance chips shown alongside the greeting, rendered as a
  // fragno-chat-action-card (its `chips` feature is exactly this). Not a
  // `.shop-assistant-msg`, so — like the typing indicator — it's invisible to
  // `_extractMessages` and never persisted. The `.shop-assistant-suggestions`
  // class stays as the marker sendMessage()/tests key off.
  _renderSuggestions() {
    const card = document.createElement("fragno-chat-action-card");
    card.className = "shop-assistant-suggestions";
    card.setAttribute("eyebrow", "Try asking");
    // fragno-chat-action-card only takes chips as a comma-separated string (no
    // array/children API), so a suggestion containing a comma would mis-split
    // into two chips. None do today; warn if that ever changes.
    if (EMPTY_STATE_SUGGESTIONS.some((s) => s.includes(","))) {
      // eslint-disable-next-line no-console
      console.warn(
        "ShopAssistant: a suggestion contains a comma — fragno chips are comma-delimited and will mis-split it.",
      );
    }
    card.setAttribute("chips", EMPTY_STATE_SUGGESTIONS.join(","));
    // Listen for the chips' underlying `fragno-button-click`, NOT the card's
    // higher-level `fragno-chat-action-card-chip-click`. The latter is the
    // documented event, but this build's fragno-chat-action-card drops its
    // chip → chip-click re-dispatch handlers when the chat-wrapper relocates
    // the card into its (thread) DOM: the chip <fragno-button>s survive as
    // markup but the card's internal `_chipHandlers` come back empty, so
    // `chip-click` never fires and the chips are dead (verified on staging —
    // `_chipHandlers.length === 0` with 3 visible chips). `fragno-button-click`
    // still fires reliably, bubbles + composes out of the card, and carries
    // the chip's text in the button's `label` attribute. The suggestions card
    // holds only chips, so any fragno-button here is a suggestion.
    // Routed through the AbortController like every other listener so destroy()
    // tears it down with the rest.
    card.addEventListener(
      "fragno-button-click",
      (e) => {
        const path =
          typeof e.composedPath === "function" ? e.composedPath() : [];
        const btn =
          path.find(
            (n) => n instanceof Element && n.tagName === "FRAGNO-BUTTON",
          ) ??
          (e.target instanceof Element
            ? e.target.closest("fragno-button")
            : null);
        const label = btn?.getAttribute("label");
        if (label) this.sendMessage(label);
      },
      { signal: this.abortController.signal },
    );
    this.messages.appendChild(card);
  }

  // Clicking a thumb toggles it: selecting the other clears any prior
  // pick, and re-clicking the already-selected thumb un-rates it.
  handleFeedback(e) {
    const btn = e.currentTarget;
    const wasSelected = btn.classList.contains(
      "shop-assistant-feedback-btn--selected",
    );
    const group = btn.closest(".shop-assistant-feedback");
    group?.querySelectorAll(".shop-assistant-feedback-btn").forEach((b) => {
      b.classList.remove("shop-assistant-feedback-btn--selected");
      b.setAttribute("aria-pressed", "false");
    });

    const thanks = group?.querySelector(".shop-assistant-feedback-thanks");
    if (wasSelected) {
      thanks?.remove();
    } else {
      btn.classList.add("shop-assistant-feedback-btn--selected");
      btn.setAttribute("aria-pressed", "true");
      if (!thanks && group) group.appendChild(this._buildFeedbackThanks());
    }

    this._persistState();
    // TODO (SG-949 follow-up): persist feedback to a backend endpoint once
    // it exists. Until then this only toggles the visual selected state
    // (locally + in sessionStorage); nothing is recorded server-side.
  }

  _persistState() {
    try {
      const state = {
        messages: this._extractMessages(),
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {
      // Private-browsing or quota errors shouldn't break the widget.
      // Persistence is best-effort — the widget still works without it.
    }
  }

  _extractMessages() {
    return Array.from(this.messages.querySelectorAll(".shop-assistant-msg"))
      .map((el) => {
        // The greeting is ephemeral — like the suggestions row and typing
        // indicator, it must never be persisted. Otherwise a greeting-only
        // widget would restore as a lone greeting on refresh and suppress
        // the empty-state chips (they're re-rendered only when nothing was
        // restored).
        if (el.dataset.ephemeral === "true") return null;
        if (el.classList.contains("shop-assistant-msg-assistant")) {
          const selected = el.querySelector(
            ".shop-assistant-feedback-btn--selected",
          );
          // Structured offer cards (real logos) were stashed here post-stream;
          // persist them so a refresh restores cards, not the prose fallback.
          let cardEnvelopes;
          try {
            cardEnvelopes = el.dataset.cardEnvelopes
              ? JSON.parse(el.dataset.cardEnvelopes)
              : undefined;
          } catch (_) {
            cardEnvelopes = undefined;
          }
          return {
            role: "assistant",
            text: el.dataset.rawText ?? "",
            feedback: selected?.dataset.feedback,
            // Whether feedback UI was shown at all (false only for the
            // greeting) — re-derived from the DOM rather than tracked
            // as separate instance state.
            showFeedback:
              el.querySelector(".shop-assistant-feedback-btn") !== null,
            cardEnvelopes,
          };
        }
        const role = el.classList.contains("shop-assistant-msg-user")
          ? "user"
          : el.classList.contains("shop-assistant-msg-error")
            ? "error"
            : el.classList.contains("shop-assistant-msg-system")
              ? "system"
              : null;
        // user/error/system are now fragno-chat-bubble elements whose text
        // lives on data-raw-text (a rendered web component's textContent is
        // unreliable). Fall back to textContent for any legacy stored node.
        return role
          ? { role, text: el.dataset.rawText ?? el.textContent ?? "" }
          : null;
      })
      .filter(Boolean);
  }

  _restoreState() {
    let stored;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      stored = JSON.parse(raw);
    } catch (_) {
      sessionStorage.removeItem(STORAGE_KEY);
      return false;
    }
    // Panel-open state is intentionally NOT restored on a fresh mount:
    // auto-reopening the panel on every page load is intrusive — especially
    // the mobile fullscreen modal, which would pop over the whole screen on
    // every page. The panel stays in its default HAML state (hidden, launcher
    // shown); the conversation below still restores and renders when the user
    // opens the panel.
    if (
      !stored ||
      !Array.isArray(stored.messages) ||
      stored.messages.length === 0
    ) {
      // No messages to restore — the caller renders the empty state
      // (greeting + chips) into the default (closed) panel.
      return false;
    }
    stored.messages.forEach((m) => {
      if (m && typeof m.role === "string" && typeof m.text === "string") {
        this._renderMessage(
          m.role,
          m.text,
          m.feedback,
          !!m.showFeedback,
          m.cardEnvelopes,
        );
      }
    });
    return true;
  }
}

export { ShopAssistant };

export function initShopAssistant(retriesLeft = 5) {
  const el = document.getElementById("shop-assistant");
  if (!el) return null;

  // Idempotent mount: a second initShopAssistant() on an already-mounted
  // element is a no-op.
  if (el.dataset.shopAssistantMounted === "true") return null;

  const instance = new ShopAssistant(el);
  // If a required child wasn't in the DOM yet (dev-server timing race
  // between the JS bundle and the HAML render), leave the guard unset
  // and retry on the next tick; without it we'd stay broken until the
  // user navigated.
  if (!instance.initialized) {
    if (retriesLeft > 0) {
      setTimeout(() => initShopAssistant(retriesLeft - 1), 50);
    }
    return null;
  }
  el.dataset.shopAssistantMounted = "true";
  return instance;
}

// Wait until every resource is loaded before attempting init, so the
// widget's HAML is guaranteed to be in the DOM. Earlier lifecycle
// events (DOMContentLoaded, module eval) can fire before HAML render
// completes under shakapacker dev-server, causing querySelector to
// return null for the child elements.
function scheduleInit() {
  if (document.readyState === "complete") {
    initShopAssistant();
  } else {
    window.addEventListener("load", () => initShopAssistant(), { once: true });
  }
}
scheduleInit();
